import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

// Types
export interface CampaignAnalytic {
  id: number;
  campaignPostId: string;
  platform: string;
  date: string;
  impressions: number;
  reach: number;
  clicks: number;
  likes: number;
  comments: number;
  shares: number;
  videoViews: number;
  opens: number;
  extraMetrics: {
    post_clicks_by_type?: Record<string, number>;
    post_reactions_by_type_total?: Record<string, number>;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CampaignWithAnalytics {
  campaign: {
    campaignPostId: string;
    campaignName: string;
    flyerUrl: string;
    validFrom: string;
    validTo: string;
  };
  analytics: CampaignAnalytic[];
}

// New interface matching actual API response structure
export interface CampaignAnalyticsData {
  campaignPostId: string;
  campaignId: string;
  status: string;
  saved: {
    [platform: string]: CampaignAnalytic[];
  };
  failed: Array<{
    platform: string;
    postId?: string;
    error: string;
  }>;
}

export interface CampaignAnalyticsState {
  data: CampaignAnalyticsData | null;
  loading: boolean;
  error: string | null;
}

const initialState: CampaignAnalyticsState = {
  data: null,
  loading: false,
  error: null
};

const campaignAnalyticsSlice = createSlice({
  name: 'campaignAnalytics',
  initialState,
  reducers: {
    fetchCampaignAnalyticsRequest: (state, action: PayloadAction<{ campaignPostId: string }>) => {
      state.loading = true;
      state.error = null;
    },
    fetchCampaignAnalyticsSuccess: (state, action: PayloadAction<CampaignAnalyticsData>) => {
      state.loading = false;
      state.data = action.payload;
      state.error = null;
    },
    fetchCampaignAnalyticsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    clearCampaignAnalytics: (state) => {
      state.data = null;
      state.loading = false;
      state.error = null;
    }
  }
});

export const {
  fetchCampaignAnalyticsRequest,
  fetchCampaignAnalyticsSuccess,
  fetchCampaignAnalyticsFailure,
  clearCampaignAnalytics
} = campaignAnalyticsSlice.actions;

export default campaignAnalyticsSlice.reducer;
